import { StackNavigationProp } from '@react-navigation/stack';
import * as Linking from 'expo-linking';
import React, { createContext, useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useNavigation } from '@react-navigation/core';
import { useUserContext } from './UserContext';
import { appcuesDidHandleURL } from '../common/appcues';
import { linkingConfiguration } from '../navigation';

export const DeepLinkContext = createContext(null);

export const DeepLinkProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const initialNavigatedRef = useRef(false);

  /**
   * Current and new screens will be automatically instrumented with deep links.
   * If you want to add a newly created screen, simply modify the 'config'
   * to include the new screen in the linkingConfiguration() function
   * located in 'src/navigation/index.tsx'
   */
  const findNavigatorForScreen = useCallback((url: string) => {
    const split = url.split('?');
    const splittedUrl = split[split.length - 1].split('=')[0];
    for (const mainNavigatorName in linkingConfiguration.config?.screens) {
      const screens = linkingConfiguration?.config?.screens;
      // drawer navigator has separate logic due to its nested structure
      if (mainNavigatorName === 'DrawerNavigator') {
        const mainNavigatorScreens = (
          screens[mainNavigatorName as keyof typeof screens] as any
        ).screens;
        for (const mainNavigatorScreenName in mainNavigatorScreens) {
          const mainNavigatorChildrenScreens = (
            mainNavigatorScreens[
              mainNavigatorScreenName as keyof typeof mainNavigatorScreens
            ] as any
          ).screens;
          for (const mainNavigatorChildScreenName in mainNavigatorChildrenScreens) {
            const mainScreens = (
              mainNavigatorChildrenScreens[
                mainNavigatorChildScreenName as keyof typeof mainNavigatorChildrenScreens
              ] as any
            ).screens;
            if (
              mainScreens &&
              Object.values(mainScreens).includes(splittedUrl)
            ) {
              const mainScreensValues = Object.values(mainScreens);
              const screenUrlIndex = mainScreensValues.findIndex(
                screen => screen === splittedUrl
              );
              const routeName = Object.keys(mainScreens)[screenUrlIndex];
              return {
                routeName,
                mainNavigatorName,
                mainNavigatorScreenName,
                mainNavigatorChildScreenName
              };
            }
          }
        }
      } else {
        const mainScreens = (
          screens[mainNavigatorName as keyof typeof screens] as any
        ).screens;
        // check if the provided URL is available inside the screens
        if (mainScreens && Object.values(mainScreens).includes(splittedUrl)) {
          const mainScreensValues = Object.values(mainScreens);
          const routeIndex = mainScreensValues.findIndex(
            screen => screen === splittedUrl
          );
          const routeName = Object.keys(mainScreens)[routeIndex];
          return { routeName, mainNavigatorName };
        }
      }
    }
    return null; // if screen is not found
  }, []);

  const handleUrlChange = useCallback(
    (url: string) => {
      if (url && navigation) {
        const { queryParams } = Linking.parse(url);
        const screen = findNavigatorForScreen(url);
        const route = (navigation as any).getCurrentRoute();
        if (queryParams !== null && Object.keys(queryParams).length) {
          // if roomId query param is detected, navigate to messenger room
          if (
            route.name !== 'SignUpEmailVerification' &&
            url.includes('email-verification') &&
            queryParams.token
          ) {
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'IntroNavigator',
                  state: {
                    index: 0,
                    routes: [
                      {
                        name: 'SignUpEmailVerificationScreen',
                        params: {
                          token: queryParams.token
                        }
                      }
                    ]
                  }
                }
              ]
            });
          } else if (
            route.name !== 'MessengerRoom' &&
            queryParams.roomId &&
            user
          ) {
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'DrawerNavigator',
                  state: {
                    index: 0,
                    routes: [
                      {
                        name: 'BottomTabNav',
                        state: {
                          index: 0,
                          routes: [
                            {
                              name: 'MessengerNav',
                              state: {
                                index: 1,
                                routes: [
                                  { name: 'MessengerRoomList' },
                                  {
                                    name: 'MessengerRoom',
                                    params: {
                                      roomId: Number(queryParams.roomId)
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            });
            // if todo param, navigate to the to do list
          } else if (queryParams.todo && route.name !== 'ToDo' && user) {
            navigation.reset({
              index: 0,
              routes: [
                {
                  name: 'DrawerNavigator',
                  state: {
                    index: 0,
                    routes: [
                      {
                        name: 'BottomTabNav',
                        state: {
                          index: 0,
                          routes: [
                            {
                              name: 'ToDoNav',
                              state: {
                                index: 0,
                                routes: [{ name: 'ToDo' }]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            });
            // check if the user's provided URL is in the app
          } else if (screen) {
            if (
              screen.mainNavigatorScreenName &&
              screen.mainNavigatorChildScreenName
            ) {
              // check if the user is logged in
              if (user) {
                navigation.reset({
                  index: 0,
                  routes: [
                    {
                      name: screen.mainNavigatorName,
                      state: {
                        index: 0,
                        routes: [
                          {
                            name: screen.mainNavigatorScreenName,
                            state: {
                              index: 0,
                              routes: [
                                {
                                  name: screen.mainNavigatorChildScreenName,
                                  state: {
                                    index: 0,
                                    routes: [{ name: screen.routeName }]
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ]
                });
              } else {
                navigation.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'IntroNavigator',
                      state: {
                        index: 0,
                        routes: [
                          {
                            name: 'LoginScreen'
                          }
                        ]
                      }
                    }
                  ]
                });
              }
            } else if (user) {
              navigation.reset({
                index: 0,
                routes: [
                  {
                    name: 'DrawerNavigator',
                    state: {
                      index: 0,
                      routes: [
                        {
                          name: 'BottomTabNav',
                          state: {
                            index: 0,
                            routes: [
                              {
                                name: 'ToDo',
                                state: {
                                  index: 0,
                                  routes: [{ name: 'ToDo' }]
                                }
                              }
                            ]
                          }
                        }
                      ]
                    }
                  }
                ]
              });
            } else {
              navigation.reset({
                index: 0,
                routes: [
                  {
                    name: screen.mainNavigatorName,
                    state: {
                      index: 0,
                      routes: [
                        {
                          name: screen.routeName
                        }
                      ]
                    }
                  }
                ]
              });
            }
          }
        }
      }
    },
    [navigation, user, findNavigatorForScreen]
  );

  useEffect(() => {
    // Disable handling of deep link on web, to avoid infinite navigation to the messenger screen
    if (Platform.OS === 'web') return;

    // handles deep links when app is running
    const linkingListener = Linking.addEventListener('url', async ({ url }) => {
      const appcuesHandledURL = await appcuesDidHandleURL(url);
      if (!appcuesHandledURL) {
        handleUrlChange(url);
      }
    });

    // handles deep links when app is not running
    Linking.getInitialURL().then(async url => {
      if (url && !initialNavigatedRef.current) {
        const appcuesHandledURL = await appcuesDidHandleURL(url);
        if (!appcuesHandledURL) {
          handleUrlChange(url);
        }
        initialNavigatedRef.current = true;
      }
    });

    return () => {
      linkingListener.remove();
    };
  }, [handleUrlChange]);

  return (
    <DeepLinkContext.Provider value={null}>{children}</DeepLinkContext.Provider>
  );
};
